import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { get, ref, push, set, remove } from 'firebase/database';
import { db } from '../common/firebase';

// Helper function to create notifications
const createNotification = async (notificationData) => {
    const notificationsRef = ref(db, 'notifications');
    const newNotificationRef = push(notificationsRef);
    await set(newNotificationRef, notificationData);
};

// Async thunk for fetching suggestions from Firebase
export const fetchsuggestions = createAsyncThunk('suggestions/fetchsuggestions', async () => {
    const suggestionsRef = ref(db, 'suggestions');
    const snapshot = await get(suggestionsRef);
    const suggestions = snapshot.val() || {};
    return Object.keys(suggestions).map(key => ({ 
        id: key, 
        ...suggestions[key],
        createdAt: Date.now(),
        lastUpdated: Number(suggestions[key].lastUpdated) || Date.now()
    }));
});

// Async thunk for adding a new suggestion
export const addsuggestion = createAsyncThunk('suggestions/addsuggestion', async (suggestionData) => {
    const timestamp = Date.now();
    const newsuggestionData = {
        ...suggestionData,
        createdAt: timestamp,
        lastUpdated: timestamp,
        likes: {},
        comments: {},
    };

    const suggestionsRef = ref(db, 'suggestions');
    const newsuggestionRef = push(suggestionsRef);
    await set(newsuggestionRef, newsuggestionData);

    // Create a notification
    await createNotification({
        type: suggestionData.type,
        contentId: newsuggestionRef.key,
        title: newsuggestionData.title,
        description: `A new suggestion titled "${newsuggestionData.title}" has been added.`,
        timestamp: timestamp,
    });

    return { id: newsuggestionRef.key, ...newsuggestionData };
});

// Async thunk for updating an existing suggestion
export const updatesuggestion = createAsyncThunk('suggestions/updatesuggestion', async ({ id, suggestionData }) => {
    const updatedsuggestionData = {
        ...suggestionData,
        lastUpdated: Date.now()
    };
    const suggestionRef = ref(db, `suggestions/${id}`);
    await set(suggestionRef, updatedsuggestionData);
    return { id, ...updatedsuggestionData };
});

// Rest of the async thunks remain the same...

// Selectors
export const selectAllsuggestions = state => state.suggestions.suggestions;

export const selectLatestsuggestions = createSelector(
    [selectAllsuggestions],
    (suggestions) => {
        return [...suggestions]
            .sort((a, b) => Number(b.createdAt) - Number(a.createdAt))
            .slice(0, 3);
    }
);

export const selectMostLikedsuggestions = createSelector(
    [selectAllsuggestions],
    (suggestions) => {
        return [...suggestions]
            .sort((a, b) => {
                const likesA = a.likes ? Object.keys(a.likes).length : 0;
                const likesB = b.likes ? Object.keys(b.likes).length : 0;
                return likesB - likesA;
            })
            .slice(0, 3);
    }
);


// Async thunk for deleting a suggestion
export const deletesuggestion = createAsyncThunk('suggestions/deletesuggestion', async (id) => {
    const suggestionRef = ref(db, `suggestions/${id}`);
    await remove(suggestionRef);
    return id;
});



// Rest of the async thunks remain the same...

const suggestSlice = createSlice({
    name: 'suggest',
    initialState: {
        suggestions: [],
        loading: false,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchsuggestions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchsuggestions.fulfilled, (state, action) => {
                state.loading = false;
                state.suggestions = action.payload;
                state.error = null;
            })
            .addCase(fetchsuggestions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(addsuggestion.fulfilled, (state, action) => {
                state.suggestions.push(action.payload);
            })
            .addCase(updatesuggestion.fulfilled, (state, action) => {
                const index = state.suggestions.findIndex(suggestion => suggestion.id === action.payload.id);
                if (index !== -1) {
                    state.suggestions[index] = action.payload;
                }
            })
            .addCase(deletesuggestion.fulfilled, (state, action) => {
                state.suggestions = state.suggestions.filter(suggestion => suggestion.id !== action.payload);
            });
    },
});

export default suggestSlice.reducer;
